import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ShieldCheck, Users, Clock, ArrowRight } from 'lucide-react';
import { useQuiz } from '../context/QuizProvider';
import { issaBadgeSquare, findBestFitHeroImage as heroImage } from '../data/mock';

export default function FindBestFitLanding() {
  const { open: openQuiz } = useQuiz();

  // Auto-open the quiz the instant a visitor lands here from a reel / share link.
  useEffect(() => {
    const t = setTimeout(() => openQuiz(), 250);
    return () => clearTimeout(t);
  }, [openQuiz]);

  return (
    <main
      data-testid="find-best-fit-landing"
      className="relative overflow-hidden bg-splash-cream"
    >

      <section className="relative max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-splash-coral/15 text-splash-coralDark px-4 py-1.5 text-xs font-semibold uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5" /> 60-Second Personalized Match
            </div>
            <h1 className="mt-5 font-display font-bold text-[2.75rem] md:text-6xl leading-[1.02] text-splash-green">
              Find the right<br />swim class for<br /><span className="text-splash-teal">your kid.</span>
            </h1>
            <p className="mt-5 text-lg text-splash-green/80 leading-relaxed max-w-xl">
              Answer a few quick questions and we'll match your child with the right Little Splash program for their age — with clear pricing, no phone tag.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={openQuiz}
                data-testid="landing-take-quiz-btn"
                className="inline-flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-7 py-3.5 font-semibold btn-transition btn-focus shadow-lg shadow-splash-coral/25"
              >
                Take the Quiz <ArrowRight className="w-4 h-4" />
              </button>
              <div className="text-xs text-splash-green/60 font-medium">Free · Under 60 seconds</div>
            </div>

            <ul className="mt-8 grid grid-cols-3 gap-3 max-w-xl">
              {[
                { icon: Clock, label: 'Instant match' },
                { icon: ShieldCheck, label: 'WSI certified' },
                { icon: Users, label: 'Sibling discounts' },
              ].map(({ icon: Icon, label }) => (
                <li key={label} className="flex flex-col items-start gap-1.5 p-3 bg-white rounded-2xl border border-splash-sand">
                  <Icon className="w-4 h-4 text-splash-teal" />
                  <span className="text-xs font-semibold text-splash-green leading-tight">{label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-splash-green/70">
              <ShieldCheck className="w-3.5 h-3.5 text-splash-teal" />
              ISSA Accredited Swim School
            </div>
          </div>

          <div className="relative fade-up">
            <div className="absolute -inset-4 md:-inset-6 rounded-[2.5rem] bg-splash-teal/20 rotate-2" />
            <img
              src={heroImage}
              alt="Instructor teaching a child to back float in a calm indoor pool"
              fetchpriority="high"
              decoding="async"
              className="relative rounded-[2rem] shadow-2xl w-full h-[340px] md:h-[460px] object-cover"
            />
            <div className="absolute -top-4 -right-4 md:-top-5 md:-right-5 bg-white rounded-2xl p-2.5 shadow-xl border border-splash-sand w-[92px] h-[92px] md:w-[110px] md:h-[110px] grid place-items-center">
            <img src={issaBadgeSquare} alt="ISSA Accredited Swim School" loading="lazy" decoding="async" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative max-w-4xl mx-auto px-6 pb-12">
        <div className="rounded-3xl bg-white border border-splash-sand p-6 md:p-8">
          <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-2">How it works</div>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-splash-green">Three questions, one clear answer.</h2>
          <ol className="mt-5 grid md:grid-cols-3 gap-4">
            {[
              { n: '1', t: 'Tell us about your swimmer(s)', d: 'Names and ages so we can match to age-appropriate classes.' },
              { n: '2', t: 'Share their comfort level', d: 'Loves the water, a little unsure, or nervous — every answer is okay.' },
              { n: '3', t: 'See the perfect fit + price', d: 'First Splash, Little Splash Foundations™, Group Membership, or Private — matched by age.' },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl bg-splash-cream/60 p-4 border border-splash-sand">
                <div className="w-7 h-7 rounded-full bg-splash-coral text-white text-sm font-bold grid place-items-center">{s.n}</div>
                <div className="mt-2 font-semibold text-splash-green">{s.t}</div>
                <div className="text-sm text-splash-green/70 mt-1">{s.d}</div>
              </li>
            ))}
          </ol>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={openQuiz}
              data-testid="landing-take-quiz-btn-secondary"
              className="inline-flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-6 py-3 font-semibold btn-transition btn-focus"
            >
              Start the Quiz <ArrowRight className="w-4 h-4" />
            </button>
            <div className="text-xs text-splash-green/60">Get your match in under a minute.</div>
          </div>
        </div>
      </section>

      <section className="relative max-w-3xl mx-auto px-6 pb-16 text-center">
        <p className="text-xs text-splash-green/50">
          Not ready for the quiz?{' '}
          <Link
            to="/about-us"
            data-testid="landing-learn-more-link"
            className="underline decoration-splash-green/30 hover:decoration-splash-teal hover:text-splash-teal transition-colors"
          >
            Learn more about Little Splash Swim
          </Link>
          .
        </p>
      </section>
    </main>
  );
}
